import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatXOF, fileUrl, BACKEND_URL } from "../lib/api";
import { isMobile } from "../lib/device";
import { WAVE_LOGO_URL, ORANGE_MONEY_LOGO_URL } from "../lib/payment-logos";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useSupport } from "../contexts/SupportContext";
import { toast } from "sonner";
import { errorToast } from "../lib/errorToast";
import { Loader2, Heart, Share2, Edit, Calendar, Users, QrCode, Copy, MessageCircle, Lock, Globe, CheckCircle2, XCircle, Smartphone, Coins, Phone } from "lucide-react";
import { normalizeSenegalPhone } from "../lib/phone";

const FALLBACK = "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1200&q=80";
const QUICK_AMOUNTS = [1000, 5000, 10000, 25000, 50000];

export default function CagnottePublic() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openSupport } = useSupport();
  const [params, setParams] = useSearchParams();
  const [cagnotte, setCagnotte] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContribute, setShowContribute] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  // Contribute form
  const [amount, setAmount] = useState(5000);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [provider, setProvider] = useState("wave");
  const [submitting, setSubmitting] = useState(false);
  const [publicCfg, setPublicCfg] = useState({ transaction_fee_pct: 1, cagnotte_fee_pct: 5 });

  // Payment result popin (success/failure)
  const [resultDialog, setResultDialog] = useState({ open: false, status: null, amount: null });

  // Orange Money QR popin
  const [orangeDialog, setOrangeDialog] = useState({ open: false, qrCode: null, deepLink: null, webLink: null, contributionId: null, status: "pending" });

  // Close + Payout dialogs
  const [closeDialog, setCloseDialog] = useState(false);
  const [closingCagnotte, setClosingCagnotte] = useState(false);
  const [reopenDialog, setReopenDialog] = useState(false);
  const [reopeningCagnotte, setReopeningCagnotte] = useState(false);

  // Edit payout phones
  const [phonesDialog, setPhonesDialog] = useState({ open: false, wave: "", orange: "", saving: false });
  const [payoutDialog, setPayoutDialog] = useState({ open: false, breakdown: null, loading: false, processing: false });
  const [payoutPhones, setPayoutPhones] = useState({ wave: "", orange: "" });
  const [savingPhones, setSavingPhones] = useState(false);

  const load = async () => {
    try {
      const [c, cl, cfg] = await Promise.all([
        api.get(`/cagnottes/${id}`),
        api.get(`/cagnottes/${id}/contributions`),
        api.get(`/platform/public-config`),
      ]);
      setCagnotte(c.data);
      setContributions(cl.data);
      setPublicCfg(cfg.data);
    } catch {
      toast.error("Cagnotte introuvable");
      navigate("/explore");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // Handle return from Wave (same-page popin instead of redirect page)
  useEffect(() => {
    const cid = params.get("cid");
    const paid = params.get("paid");
    const cancelled = params.get("cancelled");
    if (paid && cid) {
      setVerifyingPayment(true);
      api.get(`/contributions/${cid}/confirm`).then(({ data }) => {
        if (data.status === "completed") {
          setResultDialog({ open: true, status: "success", amount: data.amount });
          load();
        } else if (data.status === "pending") {
          setResultDialog({ open: true, status: "pending", amount: data.amount });
        } else {
          setResultDialog({ open: true, status: "failed", amount: data.amount });
        }
      }).catch(() => {
        setResultDialog({ open: true, status: "failed", amount: null });
      }).finally(() => {
        setVerifyingPayment(false);
        params.delete("paid"); params.delete("cid"); setParams(params, { replace: true });
      });
    } else if (cancelled) {
      setResultDialog({ open: true, status: "cancelled", amount: null });
      params.delete("cancelled"); params.delete("cid"); setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll Orange Money status while QR dialog is open
  useEffect(() => {
    if (!orangeDialog.open || !orangeDialog.contributionId) return;
    if (orangeDialog.status !== "pending") return;
    const tid = setInterval(async () => {
      try {
        const { data } = await api.get(`/contributions/${orangeDialog.contributionId}/confirm`);
        if (data.status === "completed") {
          setOrangeDialog((d) => ({ ...d, status: "completed" }));
          setResultDialog({ open: true, status: "success", amount: data.amount });
          setOrangeDialog((d) => ({ ...d, open: false }));
          load();
        } else if (data.status === "failed" || data.status === "cancelled") {
          setOrangeDialog((d) => ({ ...d, status: data.status }));
          setResultDialog({ open: true, status: data.status, amount: data.amount });
          setOrangeDialog((d) => ({ ...d, open: false }));
        }
      } catch {}
    }, 5000);
    return () => clearInterval(tid);
  }, [orangeDialog.open, orangeDialog.contributionId, orangeDialog.status]);

  const submit = async (e) => {
    e.preventDefault();
    if (amount < 100) { toast.error("Montant minimum: 100 FCFA"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contributions", {
        cagnotte_id: id,
        amount: parseFloat(amount),
        contributor_name: name,
        contributor_email: email,
        contributor_phone: phone || null,
        is_anonymous: anonymous,
        message: message || null,
        provider,
      });
      if (provider === "wave" && data.payment_url) {
        // Wave: redirect; user returns to this page with ?paid=1&cid=...
        window.location.href = data.payment_url;
      } else if (provider === "orange" && (data.qr_code || data.deep_link)) {
        // Orange Money: show QR + deeplink in popin, start polling
        setShowContribute(false);
        setOrangeDialog({
          open: true,
          qrCode: data.qr_code,
          deepLink: data.deep_link,
          webLink: data.web_link,
          contributionId: data.contribution_id,
          status: "pending",
        });
        setSubmitting(false);
      } else {
        toast.error("Réponse de paiement invalide");
        setSubmitting(false);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors du paiement";
      errorToast(openSupport, msg, {
        module: "paiement",
        subject: `Échec paiement — ${cagnotte?.title || id}`,
        message: `Ma contribution de ${amount} FCFA a échoué sur la cagnotte "${cagnotte?.title || id}".\n\nErreur : ${msg}`,
        target_id: id,
        error_code: `E_CONTRIBUTE_${err.response?.status || "NET"}`,
        error_message: msg,
      });
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.origin + `/cagnotte/${id}`);
    toast.success("Lien copié !");
  };

  const shareWhatsApp = () => {
    const url = window.location.origin + `/cagnotte/${id}`;
    const text = `Soutenez ma cagnotte « ${cagnotte.title} » sur Sen Cagnotte: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const confirmCloseCagnotte = async () => {
    setClosingCagnotte(true);
    try {
      const { data } = await api.post(`/cagnottes/${id}/close`);
      setCagnotte(data);
      toast.success("Cagnotte clôturée. Vous pouvez maintenant récupérer les fonds.");
      setCloseDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setClosingCagnotte(false);
    }
  };

  const confirmReopenCagnotte = async () => {
    setReopeningCagnotte(true);
    try {
      const { data } = await api.post(`/cagnottes/${id}/reopen`);
      setCagnotte(data);
      toast.success("Cagnotte réactivée. Les contributions sont à nouveau ouvertes.");
      setReopenDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setReopeningCagnotte(false);
    }
  };

  const savePayoutPhonesEdit = async () => {
    const wave = normalizeSenegalPhone(phonesDialog.wave);
    if (!wave) { toast.error("Numéro Wave invalide"); return; }
    const orange = normalizeSenegalPhone(phonesDialog.orange);
    if (!orange) { toast.error("Numéro Orange Money invalide"); return; }
    setPhonesDialog((d) => ({ ...d, saving: true }));
    try {
      const { data } = await api.patch(`/cagnottes/${id}/payout-phones`, { payout_wave_phone: wave, payout_orange_phone: orange });
      setCagnotte(data);
      toast.success("Numéros de reversement mis à jour");
      setPhonesDialog({ open: false, wave: "", orange: "", saving: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setPhonesDialog((d) => ({ ...d, saving: false }));
    }
  };

  const openPayoutDialog = async () => {
    setPayoutDialog({ open: true, breakdown: null, loading: true, processing: false });
    try {
      const { data } = await api.get(`/cagnottes/${id}/payout-preview`);
      setPayoutDialog({ open: true, breakdown: data, loading: false, processing: false });
      setPayoutPhones({ wave: data.payout_wave_phone || "", orange: data.payout_orange_phone || "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
      setPayoutDialog({ open: false, breakdown: null, loading: false, processing: false });
    }
  };

  const savePayoutPhones = async () => {
    if (!payoutPhones.wave || !payoutPhones.orange) {
      toast.error("Les deux numéros sont obligatoires");
      return;
    }
    setSavingPhones(true);
    try {
      await api.patch(`/cagnottes/${id}/payout-phones`, {
        payout_wave_phone: payoutPhones.wave,
        payout_orange_phone: payoutPhones.orange,
      });
      // Refresh preview to get updated phones in breakdown
      const { data } = await api.get(`/cagnottes/${id}/payout-preview`);
      setPayoutDialog({ open: true, breakdown: data, loading: false, processing: false });
      toast.success("Numéros enregistrés");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingPhones(false);
    }
  };

  const confirmPayout = async () => {
    setPayoutDialog((p) => ({ ...p, processing: true }));
    try {
      const { data } = await api.post(`/cagnottes/${id}/payout`);
      toast.success(`Reversement effectué : ${formatXOF(data.amount_paid_xof)} envoyés sur vos comptes Wave et Orange Money.`);
      setPayoutDialog({ open: false, breakdown: null, loading: false, processing: false });
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors du reversement";
      errorToast(openSupport, msg, {
        module: "paiement",
        subject: `Échec reversement — ${cagnotte?.title || id}`,
        message: `Le reversement de ma cagnotte "${cagnotte?.title || id}" a échoué.\n\nErreur : ${msg}`,
        target_id: id,
        error_code: `E_PAYOUT_${err.response?.status || "NET"}`,
        error_message: msg,
      });
      setPayoutDialog((p) => ({ ...p, processing: false }));
    }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (!cagnotte) return null;

  const pct = cagnotte.goal_amount ? Math.min(100, Math.round((cagnotte.current_amount / cagnotte.goal_amount) * 100)) : 0;
  const isOwner = user?.user_id === cagnotte.user_id;
  const goalReached = cagnotte.goal_amount && cagnotte.current_amount >= cagnotte.goal_amount;
  const completedContributions = contributions.filter((c) => c.status === "completed");
  const img = cagnotte.image_url ? fileUrl(cagnotte.image_url) : FALLBACK;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="cagnotte-page">
      {verifyingPayment && (
        <div className="mb-6 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Vérification du paiement en cours...</span>
        </div>
      )}

      {isOwner && goalReached && cagnotte.status === "active" && (
        <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-amber-50 dark:from-emerald-950/40 dark:to-amber-950/40 border-2 border-emerald-300 dark:border-emerald-700 flex items-start gap-3" data-testid="goal-reached-banner">
          <div className="h-10 w-10 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xl flex-shrink-0">🎉</div>
          <div className="flex-1">
            <p className="font-semibold text-emerald-900 dark:text-emerald-200">Bravo, votre objectif est atteint !</p>
            <p className="text-sm text-emerald-800 dark:text-emerald-300 mt-0.5">
              Vous avez collecté <strong>{formatXOF(cagnotte.current_amount)}</strong> sur les {formatXOF(cagnotte.goal_amount)} visés. La cagnotte reste ouverte — vous pouvez la clôturer quand vous le souhaitez pour récupérer les fonds.
            </p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left col */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          <div className="relative aspect-[16/9] max-h-[55vh] md:max-h-[480px] rounded-3xl overflow-hidden card-soft">
            <img src={img} alt={cagnotte.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = FALLBACK; }} />
            {cagnotte.visibility === "private" && (
              <Badge className="absolute top-4 right-4 bg-black/70 text-white border-0"><Lock className="h-3 w-3 mr-1" />Privée</Badge>
            )}
          </div>

          {completedContributions.length > 0 && (
            <div className="relative rounded-2xl border bg-card overflow-hidden" data-testid="donor-wall">
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-card to-transparent z-10 pointer-events-none" />
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-card to-transparent z-10 pointer-events-none" />
              <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/30">
                <Heart className="h-3.5 w-3.5 text-rose-500" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Donateurs récents</span>
              </div>
              <div className="py-3 overflow-hidden">
                <div className="flex gap-6 animate-scroll-x whitespace-nowrap" style={{ animation: `donor-scroll ${Math.max(20, completedContributions.length * 4)}s linear infinite` }}>
                  {[...completedContributions, ...completedContributions].map((c, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm shrink-0" data-testid={`donor-${i}`}>
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-emerald-500 to-amber-500 flex items-center justify-center text-white text-xs font-bold">
                        {(c.display_name || "?")[0].toUpperCase()}
                      </div>
                      <span className="font-medium">{c.display_name || "Anonyme"}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">{formatXOF(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground flex-wrap">
              {cagnotte.category && <Badge variant="secondary" className="rounded-full">{cagnotte.category}</Badge>}
              {cagnotte.end_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Jusqu'au {new Date(cagnotte.end_date).toLocaleDateString("fr-FR")}</span>}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">{cagnotte.title}</h1>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={cagnotte.creator_picture} />
                <AvatarFallback className="gradient-gold-green text-white">{cagnotte.creator_name?.[0] || "?"}</AvatarFallback>
              </Avatar>
              <div>
                <div className="text-sm">Organisé par</div>
                <div className="font-semibold">{cagnotte.creator_name || "Anonyme"}</div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="description" className="mt-8">
            <TabsList className="rounded-full">
              <TabsTrigger value="description" className="rounded-full" data-testid="tab-description">Description</TabsTrigger>
              <TabsTrigger value="contributions" className="rounded-full" data-testid="tab-contributions">Participants ({contributions.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="mt-6">
              <div className="card-soft p-6 sm:p-8 whitespace-pre-wrap leading-relaxed">{cagnotte.description}</div>
            </TabsContent>
            <TabsContent value="contributions" className="mt-6">
              <div className="card-soft divide-y" data-testid="contributions-list">
                {contributions.length === 0 ? (
                  <div className="p-12 text-center text-muted-foreground">Aucune contribution pour le moment. Soyez le premier !</div>
                ) : contributions.map((c) => (
                  <div key={c.id} className="p-5 flex items-start gap-4">
                    <Avatar className="h-10 w-10"><AvatarFallback className="gradient-gold-green text-white text-sm">{c.display_name[0]?.toUpperCase()}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline gap-3">
                        <div className="font-semibold truncate">{c.display_name}</div>
                        <div className="font-display font-bold gradient-text shrink-0">{formatXOF(c.amount)}</div>
                      </div>
                      {c.message && <div className="text-sm text-muted-foreground mt-1 italic">« {c.message} »</div>}
                      <div className="text-xs text-muted-foreground mt-1">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right col - Sticky donation panel */}
        <div className="lg:col-span-1 min-w-0">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="card-soft p-6 sm:p-8" data-testid="donation-panel">
              <div className="mb-5">
                {cagnotte.goal_amount ? (
                  <>
                    <div className="flex items-baseline justify-between mb-2 flex-wrap">
                      <div className="font-display text-3xl font-bold gradient-text">{formatXOF(cagnotte.current_amount)}</div>
                      <div className="text-sm text-muted-foreground">/ {formatXOF(cagnotte.goal_amount)}</div>
                    </div>
                    <Progress value={pct} className="h-2.5 [&>div]:gradient-gold-green" />
                    <div className="flex justify-between text-xs text-muted-foreground mt-2">
                      <span>{pct}% de l'objectif</span>
                      <span>{cagnotte.contributions_count} participant·s</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="font-display text-3xl font-bold gradient-text">{formatXOF(cagnotte.current_amount)}</div>
                    <div className="text-sm text-muted-foreground mt-1">collectés · {cagnotte.contributions_count} participant·s</div>
                  </>
                )}
              </div>

              {(cagnotte.status === "closed" || cagnotte.status === "settled") ? (
                <div className="text-center text-sm text-muted-foreground p-4 rounded-xl bg-muted/30 border" data-testid="cagnotte-closed-banner">
                  <Lock className="h-4 w-4 inline mr-1.5" /> {cagnotte.status === "settled" ? "Cagnotte clôturée définitivement — fonds versés" : "Cagnotte clôturée — les contributions sont fermées"}
                </div>
              ) : !showContribute ? (
                <Button onClick={() => setShowContribute(true)} className="w-full btn-gradient rounded-full h-12 text-base font-medium" data-testid="contribute-cta">
                  <Heart className="h-4 w-4 mr-2" /> Contribuer
                </Button>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contribute-form">
                  <div>
                    <Label>Montant (FCFA)</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2 mb-2">
                      {QUICK_AMOUNTS.map((a) => (
                        <button key={a} type="button" onClick={() => setAmount(a)} className={`py-2 rounded-lg text-sm font-semibold border transition ${amount === a ? "gradient-gold-green text-white border-transparent" : "border-input hover:border-primary"}`} data-testid={`amount-${a}`}>
                          {a >= 1000 ? `${a/1000}k` : a}
                        </button>
                      ))}
                      <button type="button" onClick={() => setAmount(parseInt(amount) + 1000)} className="py-2 rounded-lg text-sm font-semibold border hover:border-primary">+1k</button>
                    </div>
                    <Input type="number" min="100" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11 rounded-xl" required data-testid="contribute-amount" />
                  </div>
                  <div>
                    <Label htmlFor="cname">Votre nom</Label>
                    <Input id="cname" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="contribute-name" />
                  </div>
                  <div>
                    <Label htmlFor="cemail">Email</Label>
                    <Input id="cemail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="contribute-email" />
                  </div>
                  <div>
                    <Label htmlFor="cphone">Téléphone (optionnel)</Label>
                    <Input id="cphone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+221..." className="mt-1 h-11 rounded-xl" data-testid="contribute-phone" />
                  </div>
                  <div>
                    <Label htmlFor="cmsg">Message (optionnel)</Label>
                    <Textarea id="cmsg" rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 rounded-xl" data-testid="contribute-message" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/50">
                    <Label htmlFor="anon" className="cursor-pointer">Faire un don anonyme</Label>
                    <Switch id="anon" checked={anonymous} onCheckedChange={setAnonymous} data-testid="contribute-anonymous" />
                  </div>

                  {/* Provider selector */}
                  <div>
                    <Label className="mb-2 block">Moyen de paiement</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setProvider("wave")}
                        data-testid="provider-wave"
                        className={`relative flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          provider === "wave"
                            ? "border-[#1DC8FF] bg-[#1DC8FF]/10 shadow-sm"
                            : "border-input hover:border-[#1DC8FF]/40"
                        }`}
                      >
                        <img src={WAVE_LOGO_URL} alt="Wave" className="h-12 w-12 object-contain rounded-md" />
                        <span className="text-sm font-semibold">Wave</span>
                        {provider === "wave" && <CheckCircle2 className="absolute top-1.5 right-1.5 h-4 w-4 text-[#1DC8FF]" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setProvider("orange")}
                        data-testid="provider-orange"
                        className={`relative flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          provider === "orange"
                            ? "border-[#FF7900] bg-[#FF7900]/10 shadow-sm"
                            : "border-input hover:border-[#FF7900]/40"
                        }`}
                      >
                        <img src={ORANGE_MONEY_LOGO_URL} alt="Orange Money" className="h-12 w-12 object-contain rounded-md" />
                        <span className="text-sm font-semibold">Orange Money</span>
                        {provider === "orange" && <CheckCircle2 className="absolute top-1.5 right-1.5 h-4 w-4 text-[#FF7900]" />}
                      </button>
                    </div>
                  </div>

                  {/* Fee breakdown */}
                  {Number(amount) >= 100 && (
                    <div className="rounded-xl bg-muted/40 border p-3 text-xs space-y-1" data-testid="fee-breakdown">
                      <div className="flex justify-between"><span>Contribution</span><span>{formatXOF(Number(amount))}</span></div>
                      <div className="flex justify-between text-muted-foreground"><span>Frais transaction ({publicCfg.transaction_fee_pct}%)</span><span>+ {formatXOF(Math.round(Number(amount) * publicCfg.transaction_fee_pct / 100))}</span></div>
                      <div className="flex justify-between font-semibold pt-1 border-t"><span>Total à payer</span><span>{formatXOF(Number(amount) + Math.round(Number(amount) * publicCfg.transaction_fee_pct / 100))}</span></div>
                    </div>
                  )}

                  <Button type="submit" disabled={submitting} className="w-full btn-gradient rounded-full h-12 font-medium" data-testid="contribute-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Payer {formatXOF(Number(amount) + Math.round(Number(amount) * publicCfg.transaction_fee_pct / 100))} avec {provider === "wave" ? "Wave" : "Orange Money"}</>}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">Paiement sécurisé · Wave & Orange Money</p>
                </form>
              )}
            </div>

            {/* Share */}
            <div className="card-soft p-6">
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-widest text-muted-foreground">Partager</h3>
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={copyLink} className="rounded-full" data-testid="copy-link"><Copy className="h-4 w-4" /></Button>
                <Button variant="outline" onClick={shareWhatsApp} className="rounded-full" data-testid="share-whatsapp"><MessageCircle className="h-4 w-4" /></Button>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full" data-testid="show-qr"><QrCode className="h-4 w-4" /></Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle>QR Code de la cagnotte</DialogTitle>
                    </DialogHeader>
                    <div className="flex justify-center p-6 bg-white rounded-2xl">
                      <img src={`${BACKEND_URL}/api/cagnottes/${id}/qr`} alt="QR" className="w-64 h-64" />
                    </div>
                    <p className="text-center text-sm text-muted-foreground">Scannez pour ouvrir la cagnotte</p>
                  </DialogContent>
                </Dialog>
              </div>
              {isOwner && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/50" data-testid="visibility-toggle-row">
                    <div className="flex items-center gap-2 text-sm">
                      {cagnotte.visibility === "public" ? <Globe className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4 text-amber-600" />}
                      <span className="font-medium">{cagnotte.visibility === "public" ? "Cagnotte publique" : "Cagnotte privée"}</span>
                    </div>
                    <Switch
                      checked={cagnotte.visibility === "public"}
                      disabled={cagnotte.status === "settled"}
                      onCheckedChange={async (checked) => {
                        const newVis = checked ? "public" : "private";
                        try {
                          const { data } = await api.patch(`/cagnottes/${id}/visibility`, { visibility: newVis });
                          setCagnotte(data);
                          toast.success(checked ? "Cagnotte rendue publique" : "Cagnotte rendue privée");
                        } catch {
                          toast.error("Erreur lors du changement");
                        }
                      }}
                      data-testid="visibility-toggle"
                    />
                  </div>
                  {cagnotte.status === "settled" ? (
                    <Button variant="outline" className="w-full rounded-full" disabled data-testid="edit-cagnotte-btn">
                      <Edit className="h-4 w-4 mr-2" /> Modifier
                    </Button>
                  ) : (
                    <Link to={`/cagnotte/${id}/edit`} className="block">
                      <Button variant="outline" className="w-full rounded-full" data-testid="edit-cagnotte-btn">
                        <Edit className="h-4 w-4 mr-2" /> Modifier
                      </Button>
                    </Link>
                  )}

                  {cagnotte.status !== "settled" && (
                    <Button
                      onClick={() => setPhonesDialog({ open: true, wave: cagnotte.payout_wave_phone || "", orange: cagnotte.payout_orange_phone || "", saving: false })}
                      variant="outline"
                      className="w-full rounded-full"
                      data-testid="edit-payout-phones-btn"
                    >
                      <Phone className="h-4 w-4 mr-2" /> Numéros de reversement
                    </Button>
                  )}

                  {cagnotte.status === "active" && (
                    <Button onClick={() => setCloseDialog(true)} variant="outline" className="w-full rounded-full border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-500/10" data-testid="close-cagnotte-btn">
                      <Lock className="h-4 w-4 mr-2" /> Clôturer la cagnotte
                    </Button>
                  )}
                  {cagnotte.status === "settled" && (
                    <Button variant="outline" className="w-full rounded-full border-amber-300 text-amber-700" disabled data-testid="close-cagnotte-btn">
                      <Lock className="h-4 w-4 mr-2" /> Clôturer la cagnotte
                    </Button>
                  )}

                  {cagnotte.status === "closed" && cagnotte.payout_status !== "completed" && (
                    <>
                      <Button
                        onClick={openPayoutDialog}
                        disabled={cagnotte.payout_status === "processing"}
                        className="w-full rounded-full btn-gradient disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="payout-cagnotte-btn"
                      >
                        <Coins className="h-4 w-4 mr-2" />
                        {cagnotte.payout_status === "processing" ? "Reversement en cours…" : "Récupérer les fonds"}
                      </Button>
                      {cagnotte.payout_status !== "processing" && (
                        <Button onClick={() => setReopenDialog(true)} variant="outline" className="w-full rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-500/10" data-testid="reopen-cagnotte-btn">
                          <Heart className="h-4 w-4 mr-2" /> Réactiver la cagnotte
                        </Button>
                      )}
                    </>
                  )}

                  {cagnotte.status === "settled" && (
                    <div className="text-center text-sm text-emerald-700 dark:text-emerald-300 font-medium p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200" data-testid="cagnotte-settled-banner">
                      ✓ Fonds versés — cagnotte clôturée définitivement
                    </div>
                  )}

                  {cagnotte.payout_status === "completed" && (
                    <div className="text-center text-sm text-emerald-700 dark:text-emerald-300 font-medium p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200" data-testid="payout-completed-banner">
                      ✓ Reversement effectué ({formatXOF(cagnotte.payout_amount || 0)})
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Orange Money QR/Deeplink popin — adaptive mobile vs desktop */}
      <Dialog open={orangeDialog.open} onOpenChange={(open) => setOrangeDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-md" data-testid="orange-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src={ORANGE_MONEY_LOGO_URL} alt="Orange Money" className="h-8 w-8 object-contain rounded-md" />
              Payer avec Orange Money
            </DialogTitle>
            <DialogDescription>
              {isMobile()
                ? "Ouvrez Orange Money ou Maxit pour valider votre paiement."
                : "Scannez ce QR code avec votre téléphone (app Orange Money ou Maxit)."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {isMobile() ? (
              <>
                {/* MOBILE: deeplink button primary, QR collapsed as alternative */}
                {orangeDialog.webLink && (
                  <a href={orangeDialog.webLink} className="block">
                    <Button className="w-full btn-gradient rounded-full h-12 text-base" data-testid="orange-deeplink-btn">
                      <Smartphone className="h-5 w-5 mr-2" /> Ouvrir Orange Money / Maxit
                    </Button>
                  </a>
                )}
                {orangeDialog.qrCode && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-muted-foreground py-2">Ou scannez le QR code depuis un autre appareil</summary>
                    <div className="mt-3 flex justify-center p-3 bg-white rounded-2xl border">
                      <img src={orangeDialog.qrCode} alt="QR Code Orange Money" className="w-48 h-48 object-contain" data-testid="orange-qr-image" />
                    </div>
                  </details>
                )}
              </>
            ) : (
              <>
                {/* DESKTOP: QR code prominent, no deeplink button */}
                {orangeDialog.qrCode && (
                  <div className="flex justify-center p-4 bg-white rounded-2xl border">
                    <img src={orangeDialog.qrCode} alt="QR Code Orange Money" className="w-64 h-64 object-contain" data-testid="orange-qr-image" />
                  </div>
                )}
                <p className="text-sm text-center text-muted-foreground">
                  Ouvrez l'app <strong>Orange Money</strong> ou <strong>Maxit</strong> sur votre téléphone, puis scannez ce QR code.
                </p>
              </>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground border-t pt-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>En attente de la confirmation du paiement…</span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOrangeDialog((d) => ({ ...d, open: false }))}
              className="rounded-full"
              data-testid="orange-dialog-close"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment result popin (success / failure / cancelled) */}
      <Dialog open={resultDialog.open} onOpenChange={(open) => setResultDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-sm" data-testid="payment-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resultDialog.status === "success" && <CheckCircle2 className="h-6 w-6 text-emerald-600" />}
              {(resultDialog.status === "failed" || resultDialog.status === "cancelled") && <XCircle className="h-6 w-6 text-rose-600" />}
              {resultDialog.status === "pending" && <Loader2 className="h-6 w-6 animate-spin text-amber-600" />}
              {resultDialog.status === "success" && "Paiement confirmé"}
              {resultDialog.status === "failed" && "Paiement échoué"}
              {resultDialog.status === "cancelled" && "Paiement annulé"}
              {resultDialog.status === "pending" && "Paiement en cours"}
            </DialogTitle>
            <DialogDescription>
              {resultDialog.status === "success" && (
                <>Merci pour votre contribution{resultDialog.amount ? <> de <strong>{formatXOF(resultDialog.amount)}</strong></> : ""}. La cagnotte a été mise à jour.</>
              )}
              {resultDialog.status === "failed" && "Votre paiement n'a pas pu être finalisé. Vous pouvez réessayer."}
              {resultDialog.status === "cancelled" && "Vous avez annulé le paiement. Aucune somme n'a été débitée."}
              {resultDialog.status === "pending" && "Votre paiement est en cours de validation. Cette page se mettra à jour automatiquement."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setResultDialog({ open: false, status: null, amount: null })} className="rounded-full" data-testid="payment-result-close">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close cagnotte confirmation */}
      <Dialog open={closeDialog} onOpenChange={setCloseDialog}>
        <DialogContent className="max-w-md" data-testid="close-cagnotte-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-amber-600" /> Clôturer la cagnotte ?</DialogTitle>
            <DialogDescription>
              Une fois clôturée, votre cagnotte n'accepte plus de contributions. Vous pourrez ensuite récupérer les fonds collectés sur vos comptes Wave et Orange Money. <strong>Vous pourrez toujours la réactiver plus tard tant que le reversement n'est pas lancé.</strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(false)} className="rounded-full">Annuler</Button>
            <Button onClick={confirmCloseCagnotte} disabled={closingCagnotte} className="rounded-full btn-gradient" data-testid="confirm-close-cagnotte">
              {closingCagnotte ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clôturer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen cagnotte confirmation */}
      <Dialog open={reopenDialog} onOpenChange={setReopenDialog}>
        <DialogContent className="max-w-md" data-testid="reopen-cagnotte-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Heart className="h-5 w-5 text-emerald-600" /> Réactiver la cagnotte ?</DialogTitle>
            <DialogDescription>
              La cagnotte redevient active et accepte à nouveau les contributions. Les fonds déjà collectés restent intacts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenDialog(false)} className="rounded-full">Annuler</Button>
            <Button onClick={confirmReopenCagnotte} disabled={reopeningCagnotte} className="rounded-full btn-gradient" data-testid="confirm-reopen-cagnotte">
              {reopeningCagnotte ? <Loader2 className="h-4 w-4 animate-spin" /> : "Réactiver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit payout phones dialog */}
      <Dialog open={phonesDialog.open} onOpenChange={(open) => setPhonesDialog((d) => ({ ...d, open }))}>
        <DialogContent className="max-w-md" data-testid="edit-payout-phones-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> Numéros de reversement</DialogTitle>
            <DialogDescription>
              Ces numéros recevront les fonds au moment du reversement. Formats acceptés : 77XXXXXXX, +221XXXXXXXXX ou 00221XXXXXXXXX.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Numéro Wave</Label>
              <Input value={phonesDialog.wave} onChange={(e) => setPhonesDialog((d) => ({ ...d, wave: e.target.value }))} placeholder="+221771787379" inputMode="tel" className="mt-1.5 h-11 rounded-xl" data-testid="edit-wave-phone-input" />
            </div>
            <div>
              <Label>Numéro Orange Money</Label>
              <Input value={phonesDialog.orange} onChange={(e) => setPhonesDialog((d) => ({ ...d, orange: e.target.value }))} placeholder="+221771787379" inputMode="tel" className="mt-1.5 h-11 rounded-xl" data-testid="edit-orange-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhonesDialog({ open: false, wave: "", orange: "", saving: false })} className="rounded-full">Annuler</Button>
            <Button onClick={savePayoutPhonesEdit} disabled={phonesDialog.saving} className="rounded-full btn-gradient" data-testid="save-edit-payout-phones">
              {phonesDialog.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout preview & confirmation */}
      <Dialog open={payoutDialog.open} onOpenChange={(open) => setPayoutDialog((p) => ({ ...p, open }))}>
        <DialogContent className="max-w-md" data-testid="payout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coins className="h-5 w-5 text-emerald-600" /> Récupérer les fonds</DialogTitle>
            <DialogDescription>
              Voici le détail du reversement vers vos comptes Wave et Orange Money. La commission Sen Cagnotte est déjà déduite. Le virement est envoyé immédiatement après confirmation.
            </DialogDescription>
          </DialogHeader>
          {payoutDialog.loading && (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          )}
          {payoutDialog.breakdown && (
            <div className="space-y-4">
              <div className="rounded-2xl border p-4 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2"><img src={WAVE_LOGO_URL} alt="Wave" className="h-5 w-5 object-contain rounded" /> Wave ({payoutDialog.breakdown.wave_contributions})</span>
                  <span className="font-medium">{formatXOF(payoutDialog.breakdown.wave_collected_xof)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2"><img src={ORANGE_MONEY_LOGO_URL} alt="OM" className="h-5 w-5 object-contain rounded" /> Orange Money ({payoutDialog.breakdown.orange_contributions})</span>
                  <span className="font-medium">{formatXOF(payoutDialog.breakdown.orange_collected_xof)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span>Total collecté</span>
                  <span className="font-semibold">{formatXOF(payoutDialog.breakdown.gross_xof)}</span>
                </div>
                <div className="flex items-center justify-between text-rose-700 dark:text-rose-400">
                  <span>Commission Sen Cagnotte ({payoutDialog.breakdown.fee_pct}%)</span>
                  <span>− {formatXOF(payoutDialog.breakdown.fee_xof)}</span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t text-base">
                  <span className="font-semibold">Vous recevrez</span>
                  <span className="font-bold text-emerald-700 dark:text-emerald-400">{formatXOF(payoutDialog.breakdown.net_xof)}</span>
                </div>
              </div>

              {(!payoutDialog.breakdown.payout_wave_phone || !payoutDialog.breakdown.payout_orange_phone) ? (
                <div className="rounded-xl border-2 border-amber-300 dark:border-amber-500/40 bg-amber-50/70 dark:bg-amber-500/10 p-4 space-y-3" data-testid="missing-phones-form">
                  <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Numéros de réception manquants</p>
                  <p className="text-xs text-amber-800 dark:text-amber-200/80">Renseignez vos numéros Wave et Orange Money pour recevoir les fonds. Format : 221XXXXXXXXX</p>
                  <div className="space-y-2">
                    <Label className="text-xs">Numéro Wave</Label>
                    <Input value={payoutPhones.wave} onChange={(e) => setPayoutPhones((p) => ({ ...p, wave: e.target.value }))} placeholder="221770000000" className="h-10 rounded-xl" data-testid="payout-wave-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Numéro Orange Money</Label>
                    <Input value={payoutPhones.orange} onChange={(e) => setPayoutPhones((p) => ({ ...p, orange: e.target.value }))} placeholder="221770000000" className="h-10 rounded-xl" data-testid="payout-orange-input" />
                  </div>
                  <Button onClick={savePayoutPhones} disabled={savingPhones} className="w-full rounded-full btn-gradient h-10" data-testid="save-payout-phones">
                    {savingPhones ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer les numéros"}
                  </Button>
                </div>
              ) : (
                <div className="rounded-xl bg-muted/40 p-3 text-xs space-y-1">
                  <div className="flex justify-between"><span>→ Wave ({payoutDialog.breakdown.payout_wave_phone})</span><span className="font-medium">{formatXOF(payoutDialog.breakdown.net_wave_xof)}</span></div>
                  <div className="flex justify-between"><span>→ Orange Money ({payoutDialog.breakdown.payout_orange_phone})</span><span className="font-medium">{formatXOF(payoutDialog.breakdown.net_orange_xof)}</span></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutDialog({ open: false, breakdown: null, loading: false, processing: false })} className="rounded-full" disabled={payoutDialog.processing}>Annuler</Button>
            <Button onClick={confirmPayout} disabled={payoutDialog.loading || payoutDialog.processing || !payoutDialog.breakdown || payoutDialog.breakdown?.net_xof <= 0 || !payoutDialog.breakdown?.payout_wave_phone || !payoutDialog.breakdown?.payout_orange_phone} className="rounded-full btn-gradient" data-testid="confirm-payout">
              {payoutDialog.processing ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Reversement en cours…</> : "Confirmer le reversement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
